import { deviceTimeZone, localDateString, type MealSlot, scaleToGrams } from '@gramas/shared'
import {
  Button,
  Chip,
  EmptyState,
  List,
  ListRow,
  NumberField,
  Segmented,
  Sheet,
  Stepper,
} from '@gramas/ui'
import { useEffect, useState } from 'react'
import { type IdentifiedFood, identifyPhoto } from '../../api/foods.js'
import type { LocalFood } from '../../db/dexie.js'
import { foodDisplayName, logFood, SLOT_LABEL, SLOT_ORDER, slotForHour } from '../../db/repo.js'
import { PersonaSpinner } from '../../glitime/PersonaSpinner.js'
import { logContext } from '../../sass/context.js'
import { sassLine } from '../../sass/engine.js'
import { maybeLogToast } from '../../sass/toast.js'
import { useSassOnce } from '../../sass/useSassOnce.js'
import { syncNow } from '../../sync/engine.js'

export interface PhotoSearchSheetProps {
  open: boolean
  photo: File | null
  onOpenChange: (open: boolean) => void
  /** Opens the ordinary quantity sheet for one food instead of logging a plate. */
  onPick: (food: LocalFood) => void
  date?: string
}

interface Line {
  /** Stable across re-points and skips; the model may name two things alike. */
  id: string
  /** What the model called it, kept so the row still says what was recognised. */
  seen: string
  candidates: LocalFood[]
  chosen: LocalFood | null
  /** One piece of it. The logged amount is this times `count`. */
  each: number
  count: number
  include: boolean
  /** Open row: the alternatives are showing. */
  expanded: boolean
}

/** What actually gets logged for a line. */
function gramsOf(line: Line): number {
  return Math.round(line.each * line.count)
}

function toLines(items: readonly IdentifiedFood[]): Line[] {
  return items.map((item) => {
    const chosen = item.candidates[0] ?? null
    return {
      id: crypto.randomUUID(),
      seen: item.name,
      candidates: item.candidates,
      chosen,
      // The photo's estimate first: it is the one thing a picture knows that a
      // catalogue cannot. A typical serving is the fallback, then 100.
      each: Math.round(item.portionG ?? chosen?.servingSizeG ?? 100),
      // Three apples are three apples. The count is kept separate from the
      // grams so correcting either one does not disturb the other — the model
      // is far better at counting pieces than at judging how much one weighs.
      count: Math.max(1, item.count),
      include: chosen != null,
      expanded: false,
    }
  })
}

function kcalOf(line: Line): number {
  if (!line.chosen) return 0
  return scaleToGrams(
    {
      kcal: line.chosen.kcal,
      protein: line.chosen.protein,
      carbs: line.chosen.carbs,
      fat: line.chosen.fat,
    },
    gramsOf(line),
  ).kcal
}

/**
 * A photograph of a plate, logged in one pass.
 *
 * The model is used strictly as a search box that can see: it returns
 * Lithuanian words and a guess at how much is there, never a calorie figure.
 * Every number comes from the catalogue row shown on the line, which is why
 * each line can be re-pointed at a different row and why the grams are an
 * editable field rather than a result.
 */
export function PhotoSearchSheet({
  open,
  photo,
  onOpenChange,
  onPick,
  date,
}: PhotoSearchSheetProps) {
  const [lines, setLines] = useState<Line[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [slot, setSlot] = useState<MealSlot>(slotForHour())
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open || !photo) return
    const url = URL.createObjectURL(photo)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [open, photo])

  useEffect(() => {
    if (!open || !photo) {
      setLines(null)
      setError(null)
      return
    }
    let cancelled = false
    setLines(null)
    setError(null)
    setSlot(slotForHour())
    void identifyPhoto(photo)
      .then((found) => {
        if (!cancelled) setLines(toLines(found))
      })
      .catch(() => {
        if (!cancelled) {
          setError(
            sassLine('error_scan', {}) ??
              'Nepavyko atpažinti nuotraukos. Pabandykite ieškoti pagal pavadinimą.',
          )
        }
      })
    return () => {
      cancelled = true
    }
  }, [open, photo])

  const noneFound = lines?.length === 0
  const emptyTitle = useSassOnce(
    noneFound ? 'photo' : null,
    () => sassLine('empty_list', { surface: 'photo' }) ?? 'Maisto neatpažinta',
  )

  const update = (index: number, patch: Partial<Line>) =>
    setLines((current) =>
      (current ?? []).map((line, i) => (i === index ? { ...line, ...patch } : line)),
    )

  const chosen = (lines ?? []).filter((line) => line.include && line.chosen != null)
  const totalKcal = chosen.reduce((sum, line) => sum + kcalOf(line), 0)

  const commit = async () => {
    if (chosen.length === 0 || saving) return
    setSaving(true)
    try {
      const logDate = date ?? localDateString(new Date(), deviceTimeZone())
      for (const line of chosen) {
        if (!line.chosen) continue
        await logFood(line.chosen, { slot, quantityG: gramsOf(line), logDate })
      }
      void syncNow()
      onOpenChange(false)
      // One remark for the whole photo, never one per recognised line.
      maybeLogToast({
        ...(await logContext(logDate, slot, Math.round(totalKcal))),
        count: chosen.length,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Kas nuotraukoje?"
      footer={
        chosen.length > 0 ? (
          <Button variant="primary" block size="lg" disabled={saving} onClick={() => void commit()}>
            {saving
              ? 'Įrašoma…'
              : `Įrašyti ${chosen.length} ${chosen.length === 1 ? 'įrašą' : 'įrašus'} · ${Math.round(totalKcal)} kcal`}
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4 py-4">
        {preview && (
          <img
            src={preview}
            alt="Įkelta nuotrauka"
            className="mx-4 max-h-44 rounded-[var(--radius-card)] border border-line bg-raised object-contain"
          />
        )}

        {lines == null && error == null && (
          <div className="flex items-center justify-center py-6">
            <PersonaSpinner surface="photo" inline />
          </div>
        )}

        {error && <p className="px-4 text-sm text-danger">{error}</p>}

        {noneFound && (
          <EmptyState
            title={emptyTitle ?? ''}
            hint="Nufotografuokite iš arčiau arba ieškokite pagal pavadinimą."
          />
        )}

        {lines != null && lines.length > 0 && (
          <Segmented
            label="Valgymas"
            value={slot}
            onValueChange={setSlot}
            className="mx-4 w-auto"
            options={SLOT_ORDER.map((s) => ({ value: s, label: SLOT_LABEL[s] }))}
          />
        )}

        {lines?.map((line, index) => (
          <section key={line.id} className="flex flex-col">
            <div className="flex flex-wrap items-center gap-2 px-4 pb-1">
              <span className="font-display text-base font-semibold text-ink">{line.seen}</span>
              {line.candidates.length === 0 && <Chip tone="warn">Nėra kataloge</Chip>}
            </div>

            {line.chosen == null ? (
              <p className="px-4 text-sm text-mute">
                Kataloge tokio produkto nėra — įrašykite jį ranka.
              </p>
            ) : (
              <>
                <List className="border-y border-line bg-surface">
                  <ListRow
                    last
                    title={foodDisplayName(line.chosen)}
                    subtitle={
                      line.include
                        ? `${line.count > 1 ? `${line.count} × ${line.each} = ` : ''}${gramsOf(line)} ${line.chosen.unit} · keisti produktą`
                        : 'Neįrašoma — bakstelėkite, kad įtrauktumėte'
                    }
                    value={`${Math.round(kcalOf(line))}`}
                    meta="kcal"
                    className={line.include ? undefined : 'opacity-50'}
                    onActivate={() =>
                      update(index, line.include ? { expanded: !line.expanded } : { include: true })
                    }
                  />
                </List>

                {line.include && (
                  <div className="flex items-end gap-3 border-b border-line bg-raised px-4 py-3">
                    <NumberField
                      label="Vieno kiekis"
                      suffix={line.chosen.unit}
                      value={line.each}
                      onChange={(value) => update(index, { each: value ?? 0 })}
                    />
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-mute">Kiek vnt.</span>
                      <div className="flex h-11 items-center">
                        <Stepper
                          value={line.count}
                          min={1}
                          max={50}
                          onChange={(next) => update(index, { count: next })}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => update(index, { include: false })}
                      className="mb-3 ml-auto shrink-0 text-sm text-mute"
                    >
                      Praleisti
                    </button>
                  </div>
                )}

                {line.expanded && line.candidates.length > 1 && (
                  <List className="border-b border-line bg-surface">
                    {line.candidates.map((food, i) => (
                      <ListRow
                        key={food.id}
                        last={i === line.candidates.length - 1}
                        title={foodDisplayName(food)}
                        subtitle={`B ${Math.round(food.protein)} · A ${Math.round(food.carbs)} · R ${Math.round(food.fat)}`}
                        value={`${Math.round(food.kcal)}`}
                        meta={`kcal/100 ${food.unit}`}
                        onActivate={() => update(index, { chosen: food, expanded: false })}
                      />
                    ))}
                  </List>
                )}
              </>
            )}
          </section>
        ))}

        {lines != null && lines.length === 1 && lines[0]?.chosen != null && (
          <button
            type="button"
            onClick={() => {
              const food = lines[0]?.chosen
              if (food) onPick(food)
            }}
            className="self-start px-4 text-sm text-accent-text"
          >
            Atidaryti įprastą kiekio langą
          </button>
        )}
      </div>
    </Sheet>
  )
}
